import type { ChipProps } from '@mui/material';
import type { CSSObject } from '@mui/system';

const layersDrawerChipLabelSx: CSSObject = {
  fontWeight: 600,
  fontVariant: 'normal',
  lineHeight: 1,
  height: 'fit-content',
  whiteSpace: 'nowrap',
};

/** Keep membership chips compact when layer titles wrap or scale up. */
const layersDrawerMembershipChipRootSx: CSSObject = {
  alignSelf: 'center',
  height: 'fit-content',
  maxHeight: 'fit-content',
  flexGrow: 0,
  flexShrink: 0,
  lineHeight: 1,
};

/** Layer drawer chips: override global `watchduty` typography. */
export const layersDrawerChipSx: CSSObject = {
  mb: 0,
  textTransform: 'none',
  ...layersDrawerMembershipChipRootSx,
  '& .MuiChip-label, & span': layersDrawerChipLabelSx,
};

/** Membership / status chips beside layer titles — scales via `watchduty` chip variant. */
export const layersDrawerInlineChipSx: CSSObject = {
  ...layersDrawerChipSx,
  ml: 0,
  textTransform: 'uppercase',
  letterSpacing: '0.02em',
};

export const mergeLayersDrawerChipSx = (...overrides: CSSObject[]): CSSObject =>
  Object.assign({}, layersDrawerChipSx, ...overrides);

export type LayersDrawerMembershipChipType =
  | 'members'
  | 'pro'
  | 'internal'
  | 'private';

const membershipChipTranslationKeys: Record<
  LayersDrawerMembershipChipType,
  string
> = {
  internal: 'internalChip.label',
  members: 'membersChip.label',
  pro: 'proChip.label',
  private: 'map.layers.private',
};

const membershipChipColor: Record<
  LayersDrawerMembershipChipType,
  ChipProps['color']
> = {
  internal: 'error',
  members: 'brand',
  pro: 'brand',
  private: 'error',
};

export const getLayersDrawerMembershipChipProps = (
  label: string,
  type: LayersDrawerMembershipChipType,
): Pick<ChipProps, 'label' | 'color' | 'variant' | 'component'> => ({
  label,
  color: membershipChipColor[type],
  variant: 'watchduty',
  component: 'span',
});

export const getLayersDrawerMembershipChipLabelKey = (
  type: LayersDrawerMembershipChipType,
): string => membershipChipTranslationKeys[type];
